import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, User, Device, Friend } from '../utils/api';
import {
  MagnifyingGlassIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  DocumentArrowDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const UserManagementEnhanced: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const queryClient = useQueryClient();

  // Advanced filters
  const [authProvider, setAuthProvider] = useState<string>('');
  const [hasDevices, setHasDevices] = useState<string>('');
  const [hasSessions, setHasSessions] = useState<string>('');
  const [emailVerified, setEmailVerified] = useState<string>('');
  const [sharingEnabled, setSharingEnabled] = useState<string>('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const { data: users, isLoading, error } = useQuery<User[]>({
    queryKey: ['users', searchTerm, activeOnly, authProvider, hasDevices, hasSessions, emailVerified, sharingEnabled, sortBy, sortOrder],
    queryFn: async () => {
      const params: {
        search?: string;
        active_only: boolean;
        limit: number;
        sort_by: string;
        sort_order: string;
        auth_provider?: string;
        has_devices?: boolean;
        has_sessions?: boolean;
        email_verified?: boolean;
        sharing_enabled?: boolean;
      } = {
        search: searchTerm || undefined,
        active_only: activeOnly,
        limit: 100,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (authProvider) params.auth_provider = authProvider;
      if (hasDevices) params.has_devices = hasDevices === 'true';
      if (hasSessions) params.has_sessions = hasSessions === 'true';
      if (emailVerified) params.email_verified = emailVerified === 'true';
      if (sharingEnabled) params.sharing_enabled = sharingEnabled === 'true';

      const response = await adminApi.getUsers(params);
      return response.data;
    },
    refetchInterval: 30000,
  });

  const toggleUserStatusMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      await adminApi.updateUserStatus(userId, isActive);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (data: { user_ids: string[]; action: 'activate' | 'deactivate' | 'delete'; reason?: string }) => {
      await adminApi.bulkUpdateUsers(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setSelectedUsers(new Set());
      setShowBulkModal(false);
    },
  });

  const { data: userDevices } = useQuery<Device[]>({
    queryKey: ['user-devices', selectedUser?.id],
    queryFn: async () => {
      if (!selectedUser) return [];
      const response = await adminApi.getDevices({ user_id: selectedUser.id });
      return response.data;
    },
    enabled: !!selectedUser,
  });

  const { data: userFriends } = useQuery<Friend[]>({
    queryKey: ['user-friends', selectedUser?.id],
    queryFn: async () => {
      if (!selectedUser) return [];
      const response = await adminApi.getUserFriends(selectedUser.id);
      return response.data;
    },
    enabled: !!selectedUser,
  });

  const handleToggleUserStatus = async (user: User) => {
    const newStatus = !user.is_active;
    const action = newStatus ? 'activate' : 'deactivate';

    if (window.confirm(`Are you sure you want to ${action} ${user.display_name}?`)) {
      toggleUserStatusMutation.mutate({ userId: user.id, isActive: newStatus });
    }
  };

  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete') => {
    const reason = prompt(`Enter reason for bulk ${action}:`);
    if (reason === null) return; // Cancelled

    if (window.confirm(`Bulk ${action} ${selectedUsers.size} user(s)?`)) {
      bulkUpdateMutation.mutate({
        user_ids: Array.from(selectedUsers),
        action,
        reason: reason || undefined,
      });
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const response = await adminApi.exportUsers(format, activeOnly);
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/json',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Export failed');
    }
  };

  const toggleUserSelection = (userId: string) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users?.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users?.map(u => u.id) || []));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (user: User) => {
    if (!user.is_active) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>;
    }
    if (!user.email_verified) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Unverified</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <span className="text-red-700">Failed to load users</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            User Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage user accounts, permissions, and status
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <span className="text-sm text-gray-700">
            Showing {users?.length || 0} users
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            {selectedUsers.size > 0 && (
              <button
                onClick={() => setShowBulkModal(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                Bulk Actions ({selectedUsers.size})
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleExport('csv')}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export JSON
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
              Search Users
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                className="admin-input pl-10"
                placeholder="Search by email, name, or friend code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quick Filters</label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              <span className="ml-2 text-sm text-gray-700">Active users only</span>
            </label>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Auth Provider</label>
              <select
                value={authProvider}
                onChange={(e) => setAuthProvider(e.target.value)}
                className="admin-input"
              >
                <option value="">All</option>
                <option value="local">Local</option>
                <option value="google">Google</option>
                <option value="github">GitHub</option>
                <option value="apple">Apple</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Has Devices</label>
              <select
                value={hasDevices}
                onChange={(e) => setHasDevices(e.target.value)}
                className="admin-input"
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Has Sessions</label>
              <select
                value={hasSessions}
                onChange={(e) => setHasSessions(e.target.value)}
                className="admin-input"
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Verified</label>
              <select
                value={emailVerified}
                onChange={(e) => setEmailVerified(e.target.value)}
                className="admin-input"
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sharing</label>
              <select
                value={sharingEnabled}
                onChange={(e) => setSharingEnabled(e.target.value)}
                className="admin-input"
              >
                <option value="">All</option>
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
          </div>
        )}

        {/* Sorting */}
        <div className="mt-4 flex items-center space-x-4">
          <div className="flex items-center">
            <ArrowsUpDownIcon className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-sm font-medium text-gray-700 mr-2">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border-gray-300 rounded-md"
            >
              <option value="created_at">Created Date</option>
              <option value="last_seen">Last Seen</option>
              <option value="email">Email</option>
              <option value="display_name">Name</option>
            </select>
          </div>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="text-sm border-gray-300 rounded-md"
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={users && users.length > 0 && selectedUsers.size === users.length}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Friend Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  VPN Sharing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Seen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users?.map((user) => (
                <tr key={user.id} className={`hover:bg-gray-50 ${selectedUsers.has(user.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                          <UserIcon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.display_name}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {user.friend_code || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.sharing_enabled ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.last_seen)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => setSelectedUser(user)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleUserStatus(user)}
                      disabled={toggleUserStatusMutation.isPending}
                      className={`${
                        user.is_active
                          ? 'text-red-600 hover:text-red-900'
                          : 'text-green-600 hover:text-green-900'
                      } disabled:opacity-50`}
                      title={user.is_active ? 'Deactivate User' : 'Activate User'}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users && users.length === 0 && (
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria.
            </p>
          </div>
        )}
      </div>

      {/* Bulk Actions Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Bulk Actions ({selectedUsers.size} users)
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => handleBulkAction('activate')}
                  disabled={bulkUpdateMutation.isPending}
                  className="w-full admin-button"
                >
                  Activate Selected Users
                </button>
                <button
                  onClick={() => handleBulkAction('deactivate')}
                  disabled={bulkUpdateMutation.isPending}
                  className="w-full admin-button-secondary"
                >
                  Deactivate Selected Users
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  disabled={bulkUpdateMutation.isPending}
                  className="w-full admin-button-danger"
                >
                  Delete Selected Users
                </button>
              </div>
              <button
                onClick={() => setShowBulkModal(false)}
                className="w-full mt-4 admin-button-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal (existing code) */}
      {selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">User Details</h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Display Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.display_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Friend Code</label>
                    <p className="mt-1 text-sm font-mono text-gray-900">
                      {selectedUser.friend_code || 'Not assigned'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.location || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedUser.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Seen</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedUser.last_seen)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">Account Status</p>
                    <p className={`text-sm ${selectedUser.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedUser.is_active ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">Email Verified</p>
                    <p className={`text-sm ${selectedUser.email_verified ? 'text-green-600' : 'text-yellow-600'}`}>
                      {selectedUser.email_verified ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">VPN Sharing</p>
                    <p className={`text-sm ${selectedUser.sharing_enabled ? 'text-green-600' : 'text-gray-600'}`}>
                      {selectedUser.sharing_enabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">Profile Complete</p>
                    <p className={`text-sm ${selectedUser.is_verified ? 'text-green-600' : 'text-yellow-600'}`}>
                      {selectedUser.is_verified ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>

                {/* Devices Section */}
                <div className="pt-4 mt-4 border-t">
                  <div className="flex items-center mb-3">
                    <DevicePhoneMobileIcon className="h-5 w-5 text-gray-500 mr-2" />
                    <h4 className="text-sm font-semibold text-gray-900">Devices ({userDevices?.length || 0})</h4>
                  </div>
                  {userDevices && userDevices.length > 0 ? (
                    <div className="space-y-2">
                      {userDevices.map((device) => (
                        <div key={device.id} className="bg-gray-50 rounded p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{device.name}</p>
                              <p className="text-xs text-gray-500">{device.platform} • {device.mesh_ip || 'No IP'}</p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              device.status === 'active' ? 'bg-green-100 text-green-800' :
                              device.status === 'paired' ? 'bg-blue-100 text-blue-800' :
                              device.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {device.status}
                            </span>
                          </div>
                          {device.last_heartbeat_at && (
                            <p className="text-xs text-gray-500 mt-1">
                              Last heartbeat: {formatDate(device.last_heartbeat_at)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No devices registered</p>
                  )}
                </div>

                {/* Friends Section */}
                <div className="pt-4 mt-4 border-t">
                  <div className="flex items-center mb-3">
                    <UserGroupIcon className="h-5 w-5 text-gray-500 mr-2" />
                    <h4 className="text-sm font-semibold text-gray-900">Friends ({userFriends?.length || 0})</h4>
                  </div>
                  {userFriends && userFriends.length > 0 ? (
                    <div className="space-y-2">
                      {userFriends.map((friendship) => (
                        <div key={friendship.friendship_id} className="bg-gray-50 rounded p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{friendship.friend.display_name}</p>
                              <p className="text-xs text-gray-500">{friendship.friend.email}</p>
                            </div>
                            {friendship.auto_approve_connections && (
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                Auto-approve
                              </span>
                            )}
                          </div>
                          {friendship.accepted_at && (
                            <p className="text-xs text-gray-500 mt-1">
                              Friends since: {formatDate(friendship.accepted_at)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No friends added</p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedUser(null)}
                  className="admin-button-secondary"
                >
                  Close
                </button>
                <button
                  onClick={() => handleToggleUserStatus(selectedUser)}
                  disabled={toggleUserStatusMutation.isPending}
                  className={selectedUser.is_active ? 'admin-button-danger' : 'admin-button'}
                >
                  {selectedUser.is_active ? 'Deactivate User' : 'Activate User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementEnhanced;
